import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
  Divider,
  Box,
  IconButton,
} from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import { DocumentChunk } from "../../types/interfaces/IConversationSettings";

export interface DocumentChunkPreviewModalProps {
  chunk: DocumentChunk | null;
  open: boolean;
  onClose: () => void;
}

export const DocumentChunkPreviewModal: React.FC<DocumentChunkPreviewModalProps> = ({
  chunk,
  open,
  onClose,
}) => {
  if (!chunk) return null;

  const handleOpenUrl = () => {
    if (chunk.url) {
      window.open(chunk.url, '_blank');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "50vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {chunk.document_title || chunk.document_source}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack direction="column" gap="1em">
          {/* Document Information */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Document Information
            </Typography>
            <Stack direction="column" gap="0.5em">
              <Stack direction="row" alignItems="center" gap="1em" flexWrap="wrap">
                <Chip
                  label={`Source: ${chunk.document_source}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Title: ${chunk.document_title || 'Unknown'}`}
                  variant="outlined"
                  size="small"
                />
                {chunk.score !== undefined && (
                  <Chip
                    label={`Relevance: ${(chunk.score * 100).toFixed(1)}%`}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  />
                )}
              </Stack>
              
              {chunk.url && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={handleOpenUrl}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Open Source Document
                </Button>
              )}
            </Stack>
          </Box>

          {/* Metadata */}
          {chunk.metadata && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Metadata
                </Typography>
                <Stack direction="column" gap="0.5em">
                  {chunk.metadata.author && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Author:</strong> {chunk.metadata.author}
                    </Typography>
                  )}
                  
                  {chunk.metadata.timestamp && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Timestamp:</strong> {new Date(chunk.metadata.timestamp).toLocaleString()}
                    </Typography>
                  )}
                  
                  {chunk.metadata.content_stats && (
                    <Stack direction="row" gap="1em" flexWrap="wrap">
                      <Chip
                        label={`${chunk.metadata.content_stats.word_count} words`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`${chunk.metadata.content_stats.char_count} characters`}
                        variant="outlined"
                        size="small"
                      />
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* Document ID */}
          {chunk.document_id && (
            <Box>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                <strong>Document ID:</strong> {chunk.document_id}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}; 